import { useQuery } from 'react-query';
import type { JsonValue } from 'type-fest';

import {
  BasePartCardData,
  queryClient,
  useRequest,
  type QueryFunction,
} from '@frontend/api';

type MaterialFamily = {
  id: number;
  name: string;
};

type Material = {
  id: number;
  name: string;
  matFamily: MaterialFamily;
};

type SupplierMaterial = {
  id: number;
  displayName: string;
  material: Material | null;
};

type Process = {
  id: number;
  name: string;
};

type Account = {
  id: number;
  name: string;
};

type Component = {
  id: number;
  material: SupplierMaterial | null;
  process: Process | null;
  account: Account | null;
};

export type PartSearchInitData = BasePartCardData & {
  partNumber: string;
  revision: string;
  isExportControlled: boolean;
  components: Component[];
  material: Material | null;
};

type GetPartSearchInitParams = {
  uuid: string;
};

export const getUsePartSearchInitKey = (params: GetPartSearchInitParams) => [
  'similarParts',
  'init',
  params.uuid,
];

export const usePartSearchInit: QueryFunction<
  PartSearchInitData,
  [GetPartSearchInitParams],
  ReturnType<typeof getUsePartSearchInitKey>
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUsePartSearchInitKey(params),
    queryFn: ({ signal }) =>
      request<PartSearchInitData & JsonValue>(
        `/similar_parts/init/${params.uuid}`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      ),
  });
};

export const invalidatePartSearchInitQuery = (
  params: GetPartSearchInitParams,
) => {
  queryClient.invalidateQueries(getUsePartSearchInitKey(params));
};
